import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Alert, FileButton, LoadingOverlay } from '@mantine/core'
import { IconAlertTriangle, IconInfoCircle, IconMovie, IconRefresh, IconTrash } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { CONVERTIBLE_VIDEO_TYPES, VIDEO_TYPES } from '../../utils/assets'
import { extractPoster, formatBytes, formatDuration, readVideo } from '../../utils/videoPoster'
import { fieldLabel } from '../../utils/uiLayoutV2'
import classes from './V2Form.module.css'

const EMPTY = { sourceAssetId: null, poster: { sourceAssetId: null, renditionAssetId: null, crop: null }, hasAudio: false }

const EXTENSION_TYPES = { mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime', webm: 'video/webm', mkv: 'video/x-matroska' }

/**
 * Reel clip (AD-205): upload or drop one video; the poster frame is taken here as WebP when the
 * browser can decode it. A clip that already fits (MP4 within size and length) is stored as
 * uploaded. Otherwise, with server conversion enabled, the file goes to the server untouched and is
 * converted to H.264 inside the profile's budget on save (the server also takes the poster when
 * this browser could not); without it, such clips are refused here.
 */
export function VideoPanel({ field, value, error, onChange, templateManifest, addAsset, assetUrls, assetSourceUrls, configuration }) {
  const { t } = useTranslation()
  const media = value?.sourceAssetId ? value : { ...EMPTY, ...(value || {}) }
  const profile = templateManifest?.assetProfiles?.[field.profile] || {}
  const posterProfile = templateManifest?.assetProfiles?.[field.posterProfile] || {}
  const maxBytes = profile.maxBytes || 4 * 1024 * 1024
  const settings = usePage().props?.studioEditorV2?.video || {}
  const convert = Boolean(settings.convert)
  const accepted = convert ? CONVERTIBLE_VIDEO_TYPES : VIDEO_TYPES
  const uploadLimit = convert ? Math.max(maxBytes, Number(settings.maxSourceBytes) || 0) : maxBytes
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [over, setOver] = useState(false)
  const [localError, setLocalError] = useState(null)
  const [facts, setFacts] = useState(null)

  const record = configuration?.assets?.[media.sourceAssetId]
  const videoUrl = media.sourceAssetId ? (assetUrls[media.sourceAssetId] || assetSourceUrls[media.sourceAssetId]) : null
  const posterId = media.poster?.renditionAssetId || media.poster?.sourceAssetId
  const posterUrl = posterId ? (assetUrls[posterId] || assetSourceUrls[posterId]) : null

  // Saved clips carry server-measured facts; a pending one is read from the file.
  useEffect(() => {
    if (!media.sourceAssetId) { setFacts(null); return undefined }
    if (record?.kind === 'video') {
      setFacts({ width: record.width, height: record.height, duration: record.durationMs / 1000, bytes: record.bytes, conversion: record.conversion || null })
      setNotice(null)
      return undefined
    }
    return undefined
  }, [media.sourceAssetId, record])

  const choose = async (picked) => {
    setLocalError(null)
    setNotice(null)
    if (!picked) return
    // Some browsers leave the type empty for MOV/MKV; the extension decides then.
    const guessed = EXTENSION_TYPES[(picked.name.split('.').pop() || '').toLowerCase()]
    const file = picked.type || !guessed ? picked : new File([picked], picked.name, { type: guessed })
    if (!accepted.includes(file.type)) {
      setLocalError(convert ? t('editorV2.form.video.formatConvert') : t('editorV2.form.video.formatMp4'))
      return
    }
    if (file.size > uploadLimit) { setLocalError(t('editorV2.form.video.tooBig', { size: formatBytes(file.size), limit: formatBytes(uploadLimit) })); return }
    setBusy(true)
    const url = URL.createObjectURL(file)
    try {
      const probe = await readVideo(url).catch(() => null)
      if (probe) probe.cleanup()
      const readable = Boolean(probe && probe.width)
      const tooLong = readable && profile.maxDurationMs && probe.duration * 1000 > profile.maxDurationMs
      const fitsAsIs = readable && file.type === 'video/mp4' && file.size <= maxBytes && !tooLong
      if (!convert) {
        if (!readable) { setLocalError(t('editorV2.form.video.unreadable')); return }
        if (tooLong) { setLocalError(t('editorV2.form.video.tooLong', { duration: formatDuration(probe.duration), limit: formatDuration(profile.maxDurationMs / 1000) })); return }
        if (file.size > maxBytes) { setLocalError(t('editorV2.form.video.tooBig', { size: formatBytes(file.size), limit: formatBytes(maxBytes) })); return }
      }
      let poster = null
      if (readable) {
        const target = posterProfile.target || {}
        poster = await extractPoster(url, { maxWidth: (target.width || 300) * 2, maxHeight: (target.height || 600) * 2 }).catch(() => null)
      }
      // A clip the server will convert is uploaded as is and not pushed into the preview.
      const videoId = addAsset(file, { allowedTypes: accepted, maxBytes: uploadLimit, uploadOnly: !fitsAsIs })
      if (!videoId) return
      const posterAssetId = poster ? addAsset(poster.file, { allowedTypes: posterProfile.mimeTypes }) : null
      setFacts({ width: readable ? probe.width : null, height: readable ? probe.height : null, duration: readable ? probe.duration : null, bytes: file.size })
      if (!fitsAsIs) {
        const limits = profile.maxDurationMs
          ? t('editorV2.form.video.limitsWithDuration', { size: formatBytes(maxBytes), duration: formatDuration(profile.maxDurationMs / 1000) })
          : formatBytes(maxBytes)
        setNotice(t(tooLong ? 'editorV2.form.video.convertNoticeCut' : 'editorV2.form.video.convertNotice', { limits }))
      }
      // hasAudio is re-measured by the server on save; until then the sound control shows.
      onChange({
        sourceAssetId: videoId,
        poster: posterAssetId ? { sourceAssetId: posterAssetId, renditionAssetId: posterAssetId, crop: null } : { ...EMPTY.poster },
        hasAudio: true,
      })
    } catch {
      setLocalError(t('editorV2.form.video.prepareFailed'))
    } finally {
      URL.revokeObjectURL(url)
      setBusy(false)
    }
  }

  const drop = {
    onDragOver: (event) => { event.preventDefault(); setOver(true) },
    onDragLeave: () => setOver(false),
    onDrop: (event) => { event.preventDefault(); setOver(false); choose(event.dataTransfer?.files?.[0]) },
  }
  const target = profile.targetDurationMs ? profile.targetDurationMs / 1000 : null
  const long = facts && target && facts.duration > target + 0.5

  return (
    <div>
      <div className={classes.media}>
        <div>
          <p className={classes.mediaLabel}>{fieldLabel(field)}</p>
          <div className={classes.mediaBox} data-empty={media.sourceAssetId ? undefined : 'true'} data-over={over ? 'true' : undefined} {...drop}>
            <LoadingOverlay visible={busy} zIndex={5} overlayProps={{ blur: 1 }} loaderProps={{ color: '#d00b65', size: 'sm' }} />
            {media.sourceAssetId ? (
              <>
                <div className={classes.mediaImage}>
                  {videoUrl ? (
                    <video className={classes.mediaVideo} src={videoUrl} poster={posterUrl || undefined} muted loop playsInline controls preload="metadata" />
                  ) : posterUrl ? <img src={posterUrl} alt="" /> : <IconMovie size={40} stroke={1.2} color="#adb5bd" />}
                </div>
                <div className={classes.mediaActions}>
                  <FileButton accept={accepted.join(',')} onChange={choose}>
                    {(buttonProps) => (
                      <button {...buttonProps} type="button" className={classes.smallButton}>
                        <IconRefresh size={14} stroke={1.8} />
                        {t('editorV2.form.common.replace')}
                      </button>
                    )}
                  </FileButton>
                  <button type="button" className={classes.iconButton} data-bordered="true" aria-label={t('editorV2.form.common.removeLabel', { label: fieldLabel(field) })}
                    onClick={() => { setLocalError(null); onChange(EMPTY) }}>
                    <IconTrash size={14} stroke={1.8} />
                  </button>
                </div>
              </>
            ) : (
              <FileButton accept={accepted.join(',')} onChange={choose}>
                {(buttonProps) => (
                  <button {...buttonProps} type="button" className={classes.dropButton}>
                    <IconMovie size={24} stroke={1.6} />
                    <span>{t('editorV2.form.common.dropLine')}<br />{convert ? t('editorV2.form.video.dropVideo') : t('editorV2.form.video.dropMp4')}</span>
                  </button>
                )}
              </FileButton>
            )}
          </div>
        </div>
        <div className={classes.sources}>
          <div className={classes.sourceBox} data-bordered="true" data-align="start">
            <p className={classes.videoFactsTitle}>{t('editorV2.form.video.budgetTitle')}</p>
            <ul className={classes.videoFacts}>
              {convert ? (
                <li>{t('editorV2.form.video.budgetConvert', { limit: formatBytes(uploadLimit), size: formatBytes(maxBytes) })}</li>
              ) : (
                <li>{t('editorV2.form.video.budgetMp4', { size: formatBytes(maxBytes) })}</li>
              )}
              {target && <li>{t('editorV2.form.video.targetLength', { seconds: Math.round(target) })}</li>}
              <li>{t('editorV2.form.video.playback')}</li>
              <li>{t('editorV2.form.video.wideFootage')}</li>
            </ul>
            {facts && (
              <p className={classes.videoFactsCurrent}>
                {t('editorV2.form.video.thisClip', { facts: [facts.duration ? formatDuration(facts.duration) : null, formatBytes(facts.bytes),
                  facts.width ? `${facts.width} × ${facts.height}` : null].filter(Boolean).join(' · ') })}
              </p>
            )}
            {facts?.conversion && (
              <p className={classes.videoFactsCurrent}>
                {t('editorV2.form.video.convertedFrom', { codec: String(facts.conversion.from?.codec || 'video').toUpperCase(), size: formatBytes(facts.conversion.from?.bytes) })}
                {facts.conversion.trimmed ? ` · ${t('editorV2.form.video.cutToLength')}` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
      {notice && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} py={6} mt={8}>
          <span style={{ fontSize: 12 }}>{notice}</span>
        </Alert>
      )}
      {long && !notice && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} py={6} mt={8}>
          <span style={{ fontSize: 12 }}>{t('editorV2.form.video.tooLongHint', { duration: formatDuration(facts.duration), seconds: Math.round(target) })}</span>
        </Alert>
      )}
      {(localError || error) && <p className={classes.error}>{localError || error}</p>}
    </div>
  )
}
